import { Controller } from "@hotwired/stimulus";
import "../stylesheets/car_report_form.css";

// The car report form checks itself on the server: while the user types, the
// values are sent to `preview` (debounced), which answers a Turbo Stream with
// the messages and hints of every field, the submit button (disabled while
// something is wrong) and the values it prefilled (the fuel price of the
// year). The rules live in CarReport; this only asks and applies the prefills.
//
// A field the user touched is never prefilled, even when they emptied it.
export default class extends Controller {
  static targets = ["prefills"];
  static values = {url: String, delay: {type: Number, default: 250}};

  // Class fields: a prefills target may connect before connect() runs.
  touched = new Set();
  timer = null;
  request = null;

  disconnect() {
    clearTimeout(this.timer);
    this.request?.abort();
  }

  changed({target}) {
    const match = /^car_report\[(\w+)\]$/.exec(target.name || "");
    if (!match) return;
    this.touched.add(match[1]);
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.preview(), this.delayValue);
  }

  async preview() {
    this.request?.abort();
    const controller = new AbortController();
    this.request = controller;

    const query = new URLSearchParams();
    for (const [name, value] of new FormData(this.element)) {
      if (name === "authenticity_token" || name === "_method" || name === "utf8") continue;
      if (typeof value === "string") query.append(name, value);
    }
    this.touched.forEach((field) => query.append("touched[]", field));

    try {
      const response = await fetch(`${this.urlValue}?${query}`, {
        headers: {Accept: "text/vnd.turbo-stream.html"},
        credentials: "same-origin",
        signal: controller.signal,
      });
      if (!response.ok) return;
      window.Turbo.renderStreamMessage(await response.text());
    } catch (error) {
      if (error.name !== "AbortError") throw error;
    }
  }

  // The prefills arrive (replaced) with every answer.
  prefillsTargetConnected(element) {
    const values = JSON.parse(element.dataset.values || "{}");
    for (const [field, value] of Object.entries(values)) {
      if (this.touched.has(field)) continue;
      const input = this.element.querySelector(`[name="car_report[${field}]"]`);
      if (input && input.value === "") input.value = value;
    }
  }

  back() {
    history.back();
  }
}
